<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin supervisor-editor">
      <q-card-section class="row">
        <div class="text-h6">
          {{ dialogTitle }}
        </div>
        <q-space />
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form @submit="onSubmit">
        <q-card-section
          class="q-py-none"
        >
          <q-table
            v-model:selected="selectedUser"
            :rows="allAvailableUsers"
            :columns="usersColumns"
            flat
            selection="single"
            title=""
            :filter="userSearch"
            row-key="id"
            class="col-12"
            :pagination="{
              sortBy: 'name',
            }"
            @row-click="onSelect"
          >
            <template #top-right>
              <q-input
                v-model="userSearch"
                borderless
                dense
                debounce="300"
                :placeholder="$t('common.buttonSearch')"
              >
                <template #append>
                  <q-icon name="search" />
                </template>
              </q-input>
            </template>
          </q-table>
        </q-card-section>
        <q-card-actions class="row">
          <q-btn
            v-if="canDelete"
            flat
            :label="$t('common.buttonDelete')"
            color="negative"
            @click="onDelete"
          />
          <q-space />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('common.buttonSave')"
            type="submit"
            color="primary"
            data-e2e="button-save"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>
<script>
import { chain, find, get, head, includes, reject } from 'lodash';
import { mapState } from 'pinia';
import Credentialing from 'src/mixins/Credentialing';
import { auth } from 'src/store/auth';

export default {
  name: 'EditSupervisor',

  mixins: [Credentialing],

  props: {
    userId: {
      type: String,
      required: false,
      default: null,
    },

    allUsers: {
      type: Array,
      required: true,
    },

    excludeUsers: {
      type: Array,
      required: false,
      default: () => ([]),
    },

    canDelete: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['hide', 'ok'],

  data() {
    return {
      selectedUser: [],
      userSearch: '',

      usersColumns: [
        {
          name: 'name',
          label: this.$t('users.modals.editUser.inputNameLabel'),
          field: 'name',
          align: 'left',
          sortable: true,
        },
        {
          name: 'email',
          label: this.$t('users.modals.editUser.inputEmailLabel'),
          field: 'email',
          align: 'left',
          sortable: true,
        },
        {
          name: 'role',
          label: this.$t('users.modals.editUser.inputRoleLabel'),
          field: 'role',
          align: 'left',
          sort: (a, b) => {
            if (a === b) {
              return 0;
            }
            return (get(this.userRoles, [a, 'level'], 0) < get(this.userRoles, [b, 'level'], 0)) ? -1 : 1;
          },
          format: (role, user) => chain(this.userPrivilegesList)
            .find({ value: user.userPrivilege || user.role })
            .get('label', this.$t('settings.users.labelUserHasNoRole'))
            .value(),

          sortable: true,
        },
      ],
    };
  },

  computed: {
    ...mapState(auth, ['userRoles', 'userPrivilegesList']),

    dialogTitle() {
      return this.userId
        ? this.$t('staff.supervisor.textEditTitle')
        : this.$t('staff.supervisor.textAddTitle');
    },

    allAvailableUsers() {
      return reject(this.allUsers, (user) => (includes(this.excludeUsers, user.userId)));
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler(newUserId) {
        const user = find(this.allUsers, { userId: newUserId });
        if (user) {
          this.selectedUser = [user];
        }
      }, // userId()
    },
  },

  methods: {

    show() {
      this.$refs.dialog.show();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      this.$emit('hide');
    },

    onSelect(evt, selectedUser) {
      const user = find(this.allUsers, { userId: selectedUser.userId });
      if (user && (user.userId !== chain(this.selectedUser).head().get('userId').value())) {
        this.selectedUser = [user];
      } else {
        this.selectedUser = [];
      }
    }, // onSelect()

    onDelete() {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('common.notifyDeleteConfirm'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.$emit('ok', { editStatus: 'delete' });
          this.hide();
        });
    }, // onDelete()

    onSubmit() {
      this.$emit('ok', head(this.selectedUser));
      this.hide();
    }, // onSubmit()

  },
};
</script>
<style lang="scss" scoped>
.supervisor-editor {
  min-width:75vw;
}
</style>
